import React, { useState } from 'react';
import { TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
interface ApplicationHubProps {
  onViewApplication?: () => void;
}
type TabFilter = 'all' | 'inProgress' | 'awaitingSignature' | 'awaitingResponses' | 'readyForReview' | 'approved' | 'rejected';
type Application = {
  id: string;
  submittedBy: string;
  status: string;
  statusIcon: string;
  statusKey: TabFilter;
  tradeRefs: ('complete' | 'pending' | 'empty')[];
  guarantors: ('complete' | 'pending' | 'empty')[];
  updated: string;
  isDunderMifflin: boolean;
  industry: string;
};
const allApplications: Application[] = [{
  id: '1',
  submittedBy: 'Dunder Mifflin',
  status: 'Ready for Review',
  statusIcon: '🔍',
  statusKey: 'readyForReview',
  tradeRefs: ['complete', 'complete', 'complete'],
  guarantors: ['complete', 'complete'],
  updated: 'Jan 15, 2026',
  isDunderMifflin: true,
  industry: 'Paper & Office Supplies'
}, {
  id: '8',
  submittedBy: 'Wonka Industries',
  status: 'Approved',
  statusIcon: '✅',
  statusKey: 'approved',
  tradeRefs: ['complete', 'complete', 'complete'],
  guarantors: ['complete', 'complete'],
  updated: 'Jan 10, 2026',
  isDunderMifflin: false,
  industry: 'Food & Beverage Manufacturing'
}, {
  id: '2',
  submittedBy: 'Wayne Enterprises',
  status: 'In Progress',
  statusIcon: '🕐',
  statusKey: 'inProgress',
  tradeRefs: ['empty', 'empty', 'empty'],
  guarantors: ['empty', 'empty'],
  updated: 'Jan 12, 2026',
  isDunderMifflin: false,
  industry: 'Technology & Defense'
}, {
  id: '3',
  submittedBy: 'Vandelay Industries',
  status: 'Rejected',
  statusIcon: '🚫',
  statusKey: 'rejected',
  tradeRefs: ['empty', 'empty', 'empty'],
  guarantors: ['empty'],
  updated: 'Jan 8, 2026',
  isDunderMifflin: false,
  industry: 'Import/Export'
}, {
  id: '4',
  submittedBy: 'Acme Corporation',
  status: 'Awaiting Signature',
  statusIcon: '✍️',
  statusKey: 'awaitingSignature',
  tradeRefs: ['empty', 'empty', 'empty'],
  guarantors: ['empty'],
  updated: 'Jan 5, 2026',
  isDunderMifflin: false,
  industry: 'Manufacturing'
}, {
  id: '6',
  submittedBy: 'Pied Piper',
  status: 'Awaiting Responses',
  statusIcon: '⏳',
  statusKey: 'awaitingResponses',
  tradeRefs: ['complete', 'pending', 'complete'],
  guarantors: ['pending'],
  updated: 'Dec 22, 2025',
  isDunderMifflin: false,
  industry: 'Technology & Software'
}];
function TradeRefIndicators({
  refs
}: {
  refs: ('complete' | 'pending' | 'empty')[];
}) {
  return <div className="flex gap-1" data-id="element-359" data-mp="21g4whkgdx36i">
      {refs.map((status, i) => <div key={i} className={`w-3 h-3 rounded-sm ${status === 'complete' ? 'bg-green-500' : status === 'pending' ? 'bg-orange-500' : 'bg-gray-300'}`} data-id="element-360" data-mp="26ey5azkq860c" data-mpi={i} />)}
    </div>;
}
function GuarantorIndicators({
  guarantors
}: {
  guarantors: ('complete' | 'pending' | 'empty')[];
}) {
  return <div className="flex gap-1" data-id="element-361" data-mp="mhz5hi0dlq7n">
      {guarantors.map((status, i) => <div key={i} className={`w-3 h-3 rounded-sm ${status === 'complete' ? 'bg-green-500' : status === 'pending' ? 'bg-orange-500' : 'bg-gray-300'}`} data-id="element-362" data-mp="iiqgeqga6gif" data-mpi={i} />)}
    </div>;
}
function IndustryBubble({
  industry
}: {
  industry: string;
}) {
  return <div className="group relative inline-block" data-id="element-363" data-mp="3sst0cq6duua">
      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center cursor-pointer hover:bg-indigo-200 transition-colors" data-id="element-364" data-mp="3tc2r65o9cx8">
        <span className="text-xs font-semibold text-indigo-700" data-id="element-365" data-mp="dilxktrwh9p1">
          {industry.charAt(0)}
        </span>
      </div>
      <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-366" data-mp="8r45ywbrolgq">
        {industry}
        <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900" data-id="element-367" data-mp="3c6rpdfh27i8b"></div>
      </div>
    </div>;
}
export function ApplicationHub({
  onViewApplication
}: ApplicationHubProps) {
  const [activeTab, setActiveTab] = useState<TabFilter>('all');
  const tabs: {
    key: TabFilter;
    label: string;
    count?: number;
  }[] = [{
    key: 'all',
    label: 'All Applications'
  }, {
    key: 'inProgress',
    label: 'In Progress',
    count: 1
  }, {
    key: 'awaitingSignature',
    label: 'Awaiting Signature',
    count: 1
  }, {
    key: 'awaitingResponses',
    label: 'Awaiting Responses',
    count: 1
  }, {
    key: 'readyForReview',
    label: 'Ready for Review',
    count: 2
  }, {
    key: 'approved',
    label: 'Approved',
    count: 1
  }, {
    key: 'rejected',
    label: 'Rejected',
    count: 1
  }];
  const filteredApplications = activeTab === 'all' ? allApplications : allApplications.filter(app => app.statusKey === activeTab);
  return <div className="p-6 max-w-[1600px] mx-auto w-full space-y-6" data-id="element-368" data-mp="15j91asy4vv8c">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-369" data-mp="10jwg0cmwuw7v">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-370" data-mp="10ibmmqcc1jbl">Application Hub</h1>
        <div className="flex gap-3" data-id="element-371" data-mp="wkdw9k6tsr6m">
          <button className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-372" data-mp="wgzvggdt8q1a">
            Manage Applications
          </button>
          <button className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium" data-id="element-373" data-mp="1oas1ks0rwv6o">
            Application Links
          </button>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-3 gap-6" data-id="element-374" data-mp="1fbn3tnx1d5vk">
        <div className="bg-white rounded-lg border-l-4 border-blue-400 shadow-sm p-6" data-id="element-375" data-mp="8u9qp7a2uhcz">
          <div className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2" data-id="element-376" data-mp="3tvtkka0krtq">
            Total Applications
          </div>
          <div className="flex items-end gap-2" data-id="element-377" data-mp="5c3x60jmcep5">
            <div className="text-4xl font-bold text-gray-900" data-id="element-378" data-mp="aaxbtwan6bqw">58</div>
            <div className="flex items-center gap-1 text-green-600 text-sm font-medium mb-1" data-id="element-379" data-mp="a81eoa6mbr3i">
              <TrendingUp className="w-4 h-4" data-id="element-380" data-mp="3sid180tu9a7j" />
              24%
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border-l-4 border-green-400 shadow-sm p-6" data-id="element-381" data-mp="3iq7dj3amdey5">
          <div className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2" data-id="element-382" data-mp="3ioe117dbs0i8">
            % Approved
          </div>
          <div className="flex items-end gap-2" data-id="element-383" data-mp="38sjdpyy5ph94">
            <div className="text-4xl font-bold text-gray-900" data-id="element-384" data-mp="38tuia0zfet1a">93%</div>
            <div className="flex items-center gap-1 text-green-600 text-sm font-medium mb-1" data-id="element-385" data-mp="3ds5ps5290rgc">
              <TrendingUp className="w-4 h-4" data-id="element-386" data-mp="2zse9tys690zp" />
              5%
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border-l-4 border-purple-400 shadow-sm p-6" data-id="element-387" data-mp="34p4pil02dukl">
          <div className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2" data-id="element-388" data-mp="2l5pdg5t7v3xj">
            Credit Approved
          </div>
          <div className="text-4xl font-bold text-gray-900" data-id="element-389" data-mp="2g7ntlinyv0l0">$450K</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-390" data-mp="2q3qsmfniuj3q">
        <div className="border-b border-gray-200 overflow-x-auto" data-id="element-391" data-mp="2q63idezitxrc">
          <div className="flex" data-id="element-392" data-mp="341xknf5shovh">
            {tabs.map(tab => <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`px-6 py-3 text-sm font-semibold whitespace-nowrap transition-colors ${activeTab === tab.key ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`} data-id="element-393" data-mp="2z572mmafcj7f">
                {tab.label}
                {tab.count !== undefined && <span className="ml-2 text-xs" data-id="element-394" data-mp="3hvezimnj6df6">({tab.count})</span>}
              </button>)}
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto" data-id="element-395" data-mp="y11ezagmtpnp">
          <table className="w-full text-sm" data-id="element-396" data-mp="y004p87fx771">
            <thead className="bg-gray-50 border-b border-gray-200" data-id="element-397" data-mp="y2w0apvtfjsb">
              <tr data-id="element-398" data-mp="12366o3nisc7p">
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-399" data-mp="120jx5yh2jmmu">
                  Submitted By
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-400" data-mp="acrx2jmjhm1u">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-401" data-mp="jcf17d6nl5yl">
                  Trade Refs
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-402" data-mp="o46i0w00bxbj">
                  Guarantors
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-403" data-mp="3rqx28uy6fl8p">
                  Group
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-404" data-mp="3f9ctjkx04ufo">
                  Updated
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-405" data-mp="3a8oucbymd7ad">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-406" data-mp="31b648t64c2rm">
              {filteredApplications.map(app => <tr key={app.id} className={`transition-all ${app.isDunderMifflin ? 'bg-indigo-50/50 hover:bg-indigo-100 cursor-pointer hover:scale-[1.002]' : 'hover:bg-indigo-50/30'}`} onClick={app.isDunderMifflin ? onViewApplication : undefined} data-id="element-407" data-mp="2wgs5qhw5tfiq">
                  <td className="px-6 py-4 font-medium text-gray-900" data-id="element-408" data-mp="2dk0mg8gmb0eo">
                    <div className="flex items-center gap-2" data-id="element-409" data-mp="2dksi1ja7sy4y">
                      {app.submittedBy}
                      {app.statusKey === 'rejected' && <div className="group relative inline-block" data-id="element-410" data-mp="164qtuymwf3dq">
                          <svg className="w-4 h-4 text-amber-500 cursor-help" fill="currentColor" viewBox="0 0 20 20" data-id="element-411" data-mp="1kuetlvffltzc">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" data-id="element-412" data-mp="s7271l99tm61" />
                          </svg>
                          <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-413" data-mp="2dlgdg9u2kdp7">
                            Fraudulent Application
                            <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900" data-id="element-414" data-mp="2l0s1rbwftarq"></div>
                          </div>
                        </div>}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-415" data-mp="2ywcko7c61sz5">
                    <div className="flex items-center gap-2" data-id="element-416" data-mp="33tm6ld0cjmi8">
                      <span className="text-lg" data-id="element-417" data-mp="33v71j3qkp7gm">{app.statusIcon}</span>
                      <span className="text-gray-700" data-id="element-418" data-mp="2q1g0kjqwqvwi">{app.status}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-419" data-mp="2u1gjohoybv9w">
                    <TradeRefIndicators refs={app.tradeRefs} data-id="element-420" data-mp="3hsvl888wm8k3" />
                  </td>
                  <td className="px-6 py-4" data-id="element-421" data-mp="3mkdpp6a4yr9t">
                    <GuarantorIndicators guarantors={app.guarantors} data-id="element-422" data-mp="3mlf3hiijdrv8" />
                  </td>
                  <td className="px-6 py-4" data-id="element-423" data-mp="9wsxi45ltra">
                    <IndustryBubble industry={app.industry} data-id="element-424" data-mp="3seijjnpohzlv" />
                  </td>
                  <td className="px-6 py-4 text-gray-600" data-id="element-425" data-mp="3scej5fqzniqc">{app.updated}</td>
                  <td className="px-6 py-4" data-id="element-426" data-mp="3ndbc5prg72fq">
                    <div className="flex justify-center" data-id="element-427" data-mp="3igbdw10ub3z6">
                      <motion.button whileHover={{
                    scale: 1.05
                  }} whileTap={{
                    scale: 0.95
                  }} onClick={app.isDunderMifflin ? onViewApplication : undefined} className={`px-6 py-2 rounded-lg text-white text-sm font-semibold transition-colors ${app.isDunderMifflin ? 'bg-indigo-600 hover:bg-indigo-700 cursor-pointer' : 'bg-indigo-400 cursor-not-allowed'}`} data-id="element-428" data-mp="3ift8oew79ff4">
                        VIEW
                      </motion.button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}